import React, {
  useImperativeHandle,
  forwardRef,
  SetStateAction,
  Dispatch,
} from 'react';

import moment from 'moment';

import TimePickerComp from '@shared/TimePicker';
import SearchCarRentalButton from '@domain/carRental/searchBar/home/searchCarRentalButton/index';
import {
  formatMomentDate,
  getNDaysLater,
  HOTEL_SEARCH_DATE_FORMAT,
  isBeforeDateTime,
} from '@utils/dateUtil';
import useSearchBar from '@contexts/searchbar';
import STATIC_ROUTES from '@data/staticRoutes.json';
import {
  CarRentalLocationType,
  CarRentalSearchBarActionType,
} from '@contexts/searchbar/types/car';
import { useVehiclePastSearches } from '@hooks/useCarRentalPastSearches';
import { VehicleOfficeSearchViewModel } from '@api/index';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import Checkbox from '@components/Checkbox';
import { getAvailableTimes } from '@shared/TimePicker/utils';
import { useAppRouter } from '@contexts/router';
import { useBreakpoint } from '@contexts/breakpoints';
import revalidate from 'app/actions/revalidatePath';
import Loader from '@components/Loader/Loader';

import DateSelection from './dateSelection';
import {
  StyledContent,
  StyledLayout,
  DateWrapper,
  ButtonWrapper,
  TimepickerWrapper,
  CheckboxWrapper,
} from './styled';
import CarRentalSelection from './carRentalSelection';
import {
  Arrow,
  ErrorMessage,
  ErrorMessageContainer,
} from './dateSelection/styled';

interface ICarRentalSearch {
  handleDrawerOpen?: () => void;
  page?: string;
  alignVertical?: boolean;
  setIsRedirecting?: Dispatch<SetStateAction<boolean>>;
}

export type ICarSearchBarHandle = {
  triggerHandleSubmit: () => void;
};

const CarRentalHomeSearchBarContent = forwardRef<
  ICarSearchBarHandle,
  ICarRentalSearch
>(({ handleDrawerOpen, page, alignVertical, setIsRedirecting }, ref) => {
  const isHomePage = page === 'home';
  const isBasketPage = page === 'basket';
  const isListingPage = page === 'list';

  const { pastVehicleItems, saveVehicleSearch } = useVehiclePastSearches();

  /**
   * This function is used to trigger the submit button from outside the component.
   * It is used in the drawer search bar in the hotel listing mobile header.
   */
  useImperativeHandle(ref, () => ({
    triggerHandleSubmit() {
      handleSearch();
    },
  }));

  const { push, isLoading } = useAppRouter();
  const {
    state: { car: state },
    dispatch,
  } = useSearchBar();
  const { xl: isDesktop } = useBreakpoint();

  const pickUpDay = moment(state.pickUpDate).locale('en').format('dddd');

  const dropOffDay = moment(state.dropOffDate).locale('en').format('dddd');

  const workingDaysForPickup = state.pickUpLocation?.workingDays?.find(
    (days) => days.day === pickUpDay
  );
  const workingDaysForDropOff = state[
    state.isDifferentLocation ? 'dropOffLocation' : 'pickUpLocation'
  ]?.workingDays?.find((days) => days.day === dropOffDay);

  const errors = state.errors;

  /**
   * This function is used to check the form states.
   * @returns {boolean} Returns true if the form is valid.
   */
  const checkFormValid = () => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_ERROR,
      payload: {
        pickUpLocation: !state.pickUpLocation,
        dropOffLocation: !state.dropOffLocation,
        pickUpDate: !state.pickUpDate,
        dropOffDate: !state.dropOffDate,
        pickUpTime: !state.pickUpTime,
        dropOffTime: !state.dropOffTime,
      },
    });

    return (
      !!state.pickUpLocation &&
      !!state.dropOffLocation &&
      !!state.pickUpDate &&
      !!state.dropOffDate &&
      !!state.pickUpTime &&
      !!state.dropOffTime
    );
  };

  const isAnyStateNull =
    !state.pickUpLocation ||
    !state.dropOffLocation ||
    !state.dropOffTime ||
    !state.pickUpTime ||
    !state.dropOffDate ||
    !state.pickUpDate;
  const hasError = Object.values(state.errors).some((error) => error);

  const handleSearch = async () => {
    const isValid = checkFormValid();

    if (!isValid) {
      return;
    }

    saveVehicleSearch({
      arr: state?.pickUpLocation as VehicleOfficeSearchViewModel,
      dep: state?.dropOffLocation as VehicleOfficeSearchViewModel,
    });
    handleDrawerOpen?.();
    setIsRedirecting?.(true);

    const pathname = STATIC_ROUTES.carRentalSearchPathPrefix;
    await revalidate(pathname);

    push({
      pathname,
      query: {
        pickUpLocation: state?.pickUpLocation?.slug ?? '',
        pickUpDate: formatMomentDate(
          state.pickUpDate,
          HOTEL_SEARCH_DATE_FORMAT
        ),
        pickUpTime: state.pickUpTime,
        dropOffLocation: state.dropOffLocation?.slug ?? '',
        dropOffTime: state.dropOffTime,
        dropOffDate: formatMomentDate(
          state.dropOffDate,
          HOTEL_SEARCH_DATE_FORMAT
        ),
      },
    });
  };

  const isSameDate =
    formatMomentDate(state.dropOffDate) === formatMomentDate(state.pickUpDate);

  const handleDropUpTime = (value) => {
    if (value?.split(':')[0] < state.pickUpTime?.split(':')[0] && isSameDate) {
      dispatch({
        type: CarRentalSearchBarActionType.SET_PICK_UP_TIME,
        payload: value,
      });
      dispatch({
        type: CarRentalSearchBarActionType.SET_DROP_OFF_TIME,
        payload: value,
      });
    } else {
      dispatch({
        type: CarRentalSearchBarActionType.SET_DROP_OFF_TIME,
        payload: value,
      });
    }
  };
  const handlePickUpTime = (value) => {
    if (state.dropOffTime?.split(':')[0] < value?.split(':')[0] && isSameDate) {
      dispatch({
        type: CarRentalSearchBarActionType.SET_PICK_UP_TIME,
        payload: value,
      });
      dispatch({
        type: CarRentalSearchBarActionType.SET_DROP_OFF_TIME,
        payload: value,
      });
    } else {
      dispatch({
        type: CarRentalSearchBarActionType.SET_PICK_UP_TIME,
        payload: value,
      });
    }
  };

  const handleChange = (
    type: 'pickUp' | 'dropOff',
    value: CarRentalLocationType
  ) => {
    if (type === 'dropOff') {
      dispatch({
        type: CarRentalSearchBarActionType.SET_DROP_OFF_LOCATION,
        payload: value || null,
      });

      const dropOffDay = moment(state.dropOffDate).locale('en').format('dddd');
      const newWorkingDay = value?.workingDays?.find(
        (days) => days.day === dropOffDay
      );

      const newAvailableTimes = getAvailableTimes(
        newWorkingDay,
        state.dropOffDate?.toDate()
      );
      const nearestTime = newAvailableTimes[0];

      dispatch({
        type: CarRentalSearchBarActionType.SET_DROP_OFF_TIME,
        payload: newAvailableTimes.includes(state.dropOffTime)
          ? state.dropOffTime
          : nearestTime,
      });
    } else if (type === 'pickUp') {
      dispatch({
        type: CarRentalSearchBarActionType.SET_PICK_UP_LOCATION,
        payload: value || null,
      });

      const pickUpDay = moment(state.pickUpDate).locale('en').format('dddd');
      const newWorkingDay = value?.workingDays?.find(
        (days) => days.day === pickUpDay
      );

      const newAvailableTimes = getAvailableTimes(
        newWorkingDay,
        state.pickUpDate?.toDate()
      );
      const nearestTime = newAvailableTimes[0];

      dispatch({
        type: CarRentalSearchBarActionType.SET_PICK_UP_TIME,
        payload: newAvailableTimes.includes(state.pickUpTime)
          ? state.pickUpTime
          : nearestTime,
      });
    }
  };

  const handleDifferentLocationClick = () => {
    if (state.isDifferentLocation) {
      handleChange('dropOff', state.pickUpLocation);
      dispatch({
        type: CarRentalSearchBarActionType.SET_IS_DIFFERENT_LOCATION,
        payload: false,
      });
    } else {
      handleChange('dropOff', null);
      dispatch({
        type: CarRentalSearchBarActionType.SET_IS_DIFFERENT_LOCATION,
        payload: true,
      });
    }
  };

  const handleChangePickupDate = (value) => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_PICK_UP_DATE,
      payload: moment(value),
    });

    if (
      isBeforeDateTime(moment(state.dropOffDate), moment(value)) ||
      state.dropOffDate === null
    ) {
      dispatch({
        type: CarRentalSearchBarActionType.SET_DROP_OFF_DATE,
        payload: getNDaysLater(moment(value), 1),
      });

      // set drop off time to the nearest available time
      const dropOffLocation = state.dropOffLocation;
      const workingDayForDropOff = dropOffLocation?.workingDays?.find(
        (days) =>
          days.day ===
          getNDaysLater(moment(value), 1).locale('en').format('dddd')
      );

      const availableDropOffTimes = getAvailableTimes(
        workingDayForDropOff,
        value
      );
      const nearestDropOffTime = availableDropOffTimes[0];

      dispatch({
        type: CarRentalSearchBarActionType.SET_DROP_OFF_TIME,
        payload: availableDropOffTimes.includes(state.dropOffTime)
          ? state.dropOffTime
          : nearestDropOffTime,
      });
    }

    const pickUpLocation = state.pickUpLocation;
    const workingDayForPickup = pickUpLocation?.workingDays?.find(
      (days) => days.day === moment(value).locale('en').format('dddd')
    );

    const availableTimes = getAvailableTimes(workingDayForPickup, value);
    const nearestTime = availableTimes[0];

    dispatch({
      type: CarRentalSearchBarActionType.SET_PICK_UP_TIME,
      payload: availableTimes.includes(state.pickUpTime)
        ? state.pickUpTime
        : nearestTime,
    });
  };

  const handleChangeDropOffDate = (value) => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_DROP_OFF_DATE,
      payload: moment(value),
    });

    const dropOffLocation = state.isDifferentLocation
      ? state.dropOffLocation
      : state.pickUpLocation;

    const workingDayForDropOff = dropOffLocation?.workingDays?.find(
      (days) => days.day === moment(value).locale('en').format('dddd')
    );

    const availableTimes = getAvailableTimes(workingDayForDropOff, value);
    const nearestTime = availableTimes[0];

    dispatch({
      type: CarRentalSearchBarActionType.SET_DROP_OFF_TIME,
      payload: availableTimes.includes(state.dropOffTime)
        ? state.dropOffTime
        : nearestTime,
    });
  };

  return (
    <>
      <StyledLayout
        $whiteBg={alignVertical || isListingPage}
        isHomePage={isHomePage}
      >
        <StyledContent alignVertical={!isDesktop || alignVertical}>
          <CarRentalSelection
            onChange={(value) => {
              handleChange('pickUp', value);
              if (!state.isDifferentLocation) {
                handleChange('dropOff', value);
              }
            }}
            label='Alış yeri yazınız..'
            activeLocation={state.pickUpLocation}
            history={pastVehicleItems}
            alignVertical={alignVertical}
            isLocationError={state.errors.pickUpLocation}
            setIsLocationError={(value) =>
              dispatch({
                type: CarRentalSearchBarActionType.SET_ERROR,
                payload: {
                  ...state.errors,
                  pickUpLocation: value,
                },
              })
            }
          />

          <LocationAnimationErrorBox
            hasError={errors.pickUpDate && !state.pickUpDate}
          >
            <DateWrapper>
              <DateSelection
                title='Alış tarihi'
                date={state.pickUpDate}
                setDate={handleChangePickupDate}
              />
              {state.errors.pickUpDate && !state.pickUpDate && (
                <ErrorMessageContainer>
                  <Arrow />
                  <ErrorMessage>Tarih seçiniz</ErrorMessage>
                </ErrorMessageContainer>
              )}
            </DateWrapper>
          </LocationAnimationErrorBox>
          <LocationAnimationErrorBox
            hasError={errors.pickUpTime && !state.pickUpTime}
          >
            <TimepickerWrapper>
              <TimePickerComp
                title='Alış saati'
                time={state.pickUpTime}
                onChange={handlePickUpTime}
                availableTimes={getAvailableTimes(
                  workingDaysForPickup,
                  state.pickUpDate?.toDate()
                )}
              />
              {state.errors.pickUpTime && !state.pickUpTime && (
                <ErrorMessageContainer>
                  <Arrow />
                  <ErrorMessage>Saat seçiniz</ErrorMessage>
                </ErrorMessageContainer>
              )}
            </TimepickerWrapper>
          </LocationAnimationErrorBox>

          {state.isDifferentLocation ? (
            <div style={{ position: 'relative' }}>
              <CarRentalSelection
                onChange={(value) => handleChange('dropOff', value)}
                label='Teslim yeri yazınız..'
                activeLocation={state.dropOffLocation}
                history={pastVehicleItems}
                isLocationError={errors.dropOffLocation}
                alignVertical={alignVertical}
                setIsLocationError={(value) =>
                  dispatch({
                    type: CarRentalSearchBarActionType.SET_ERROR,
                    payload: {
                      ...state.errors,
                      dropOffLocation: value,
                    },
                  })
                }
              />
              {state.errors.dropOffLocation && !state.dropOffLocation && (
                <ErrorMessageContainer>
                  <Arrow />
                  <ErrorMessage>Lokasyon seçiniz</ErrorMessage>
                </ErrorMessageContainer>
              )}
            </div>
          ) : null}

          <LocationAnimationErrorBox
            hasError={errors.dropOffDate && !state.dropOffDate}
          >
            <DateWrapper>
              <DateSelection
                title='Teslim Tarihi'
                date={state.dropOffDate}
                setDate={handleChangeDropOffDate}
                isOutsideRange={(day) => day.diff(moment(state.pickUpDate)) < 0}
              />
              {state.errors.dropOffDate && !state.dropOffDate && (
                <ErrorMessageContainer>
                  <Arrow />
                  <ErrorMessage>Tarih seçiniz</ErrorMessage>
                </ErrorMessageContainer>
              )}
            </DateWrapper>
          </LocationAnimationErrorBox>

          <LocationAnimationErrorBox
            hasError={errors.dropOffTime && !state.dropOffTime}
          >
            <TimepickerWrapper>
              <TimePickerComp
                title='Teslim saati'
                onChange={handleDropUpTime}
                time={state.dropOffTime}
                containerClassName='is-bordered'
                availableTimes={getAvailableTimes(
                  workingDaysForDropOff,
                  state.dropOffDate?.toDate()
                )}
              />

              {state.errors.dropOffTime && !state.dropOffTime && (
                <ErrorMessageContainer>
                  <Arrow />
                  <ErrorMessage>Saat seçiniz</ErrorMessage>
                </ErrorMessageContainer>
              )}
            </TimepickerWrapper>
          </LocationAnimationErrorBox>

          {!isDesktop || alignVertical ? (
            <CheckboxWrapper>
              <Checkbox
                className='checkbox-responsive'
                title='Farklı yerde bırakmak istiyorum'
                checked={state.isDifferentLocation}
                onChange={handleDifferentLocationClick}
                value={state.isDifferentLocation}
              />
            </CheckboxWrapper>
          ) : null}

          {!isDesktop && !(isHomePage || isBasketPage) ? null : (
            <ButtonWrapper alignVertical={alignVertical}>
              <SearchCarRentalButton
                onClick={handleSearch}
                isHomePage={isHomePage}
                disabled={hasError && isAnyStateNull}
              />
            </ButtonWrapper>
          )}
        </StyledContent>
        {isDesktop && !alignVertical ? (
          <CheckboxWrapper>
            <Checkbox
              className='checkbox-desktop'
              title='Farklı yerde bırakmak istiyorum'
              checked={state.isDifferentLocation}
              onChange={handleDifferentLocationClick}
              value={state.isDifferentLocation}
            />
          </CheckboxWrapper>
        ) : null}
      </StyledLayout>
      {isLoading && !isListingPage && <Loader />}
    </>
  );
});

export default CarRentalHomeSearchBarContent;
